import { useEffect, useRef } from 'react';

import { css } from '@compiled/react';
// @ts-expect-error -- react-intersection-observer resolves as ESM under Node16; matches sibling sections (e.g. future-of-teamwork.tsx)
import { useInView } from 'react-intersection-observer';
import { FormattedMessage } from 'react-intl';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import UFOSegment from '@atlaskit/react-ufo/segment';
import { token } from '@atlaskit/tokens';

import { Adg4Button } from '@/components/adg4/button/src/button';
import { useLocalizedHref } from '@/libraries/web-framework/src/localization/use-localized-href';
import { Row } from '@/libraries/wpl/foundations/src/grid';
import { experiments } from '@/utils/experiment/manifest';
import { useExperiment } from '@/utils/experiment/use-experiment';

/**
 * CustomersPrimaryCtaBand
 *
 * Experiment (twcg-2273-customers-page-primary-cta): a page-native, direct-to-signup
 * CTA band shown to logged-out visitors on the /customers index, inserted directly below
 * the hero. Treatment routes straight to Jira signup, bypassing the global-nav product
 * picker.
 *
 * The experiment is evaluated before the control `return null`, so both enrolled
 * arms are exposed symmetrically. The separate section scroll event below is treatment-only
 * tracking for band visibility, not experiment exposure.
 */
export const CustomersPrimaryCtaBand = () => {
	const experiment = useExperiment(experiments.twcg2273CustomersPagePrimaryCta);
	const signupHref = useLocalizedHref('/try/cloud/signup?bundle=jira-software&edition=free');

	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { ref: bandRef, inView } = useInView({ threshold: 0.5, triggerOnce: true });
	const hasFiredSectionReachRef = useRef(false);

	const isEnrolledTreatment = experiment.type === 'enrolled' && experiment.value === true;

	// Track treatment section reach once using WAC's section scroll event shape;
	// experiment exposure is handled by `useExperiment`.
	useEffect(() => {
		if (isEnrolledTreatment && inView && !hasFiredSectionReachRef.current) {
			hasFiredSectionReachRef.current = true;
			createAnalyticsEvent({
				eventType: 'ui',
				action: 'scrolledTo',
				actionSubject: 'section',
				source: window.location.pathname,
				attributes: {
					section: 'customersPrimaryCtaBand',
					productKey: 'customers',
					labelEnglish: 'Get started with Jira',
				},
			}).fire();
		}
	}, [isEnrolledTreatment, inView, createAnalyticsEvent]);

	if (!isEnrolledTreatment) {
		return null;
	}

	return (
		<UFOSegment name="customers-page-primary-cta-band">
			<section css={sectionStyles} ref={bandRef}>
				<Row layout="8">
					<div css={contentWrapperStyles}>
						<h2 css={headingStyles}>
							<FormattedMessage
								id="CustomersLandingPage.primaryCtaBand.heading"
								defaultMessage="Start your own success story"
							/>
						</h2>
						<Adg4Button
							variant="primary"
							color="blue"
							href={signupHref}
							dataEventProps={{
								'data-event': 'clicked',
								'data-event-component': 'linkButton',
								'data-event-container': 'ctaSection',
								'data-product-key': 'customers',
								'data-label-english': 'Get started with Jira',
							}}
						>
							<FormattedMessage
								id="CustomersLandingPage.primaryCtaBand.button"
								defaultMessage="Get started with Jira"
							/>
						</Adg4Button>
					</div>
				</Row>
			</section>
		</UFOSegment>
	);
};

const sectionStyles = css({
	maxWidth: '1440px',
	margin: '0 auto',
	// No vertical padding: the hero (CarouselSection) contributes its margin-bottom
	// and the story filters contribute their margin-top, so the band bridges the two
	// without stacking its own vertical space on top. Horizontal padding stays 0.
	padding: '0',
	textAlign: 'center',
});

const contentWrapperStyles = css({
	margin: '0 auto',
	maxWidth: '568px',
	display: 'flex',
	flexDirection: 'column',
	alignItems: 'center',
	gap: token('space.300'),
});

const headingStyles = css({
	maxWidth: '510px',
	marginBlockStart: token('space.0'),
	marginBlockEnd: token('space.0'),
});
